import { useParams, Link } from "react-router-dom";
import { ScrollReveal } from "@/components/base/ScrollAnimations";
import { findCourseBySlug, getRelatedCourses } from "@/mocks/capacityCoursesData";
import { useEffect } from "react";

const programmeColors: Record<string, { badge: string; accent: string; bg: string }> = {
  "black-belt": {
    badge: "bg-tie-teal/10 text-tie-teal border-tie-teal/20",
    accent: "bg-tie-teal",
    bg: "bg-tie-navy",
  },
  "factory-skills": {
    badge: "bg-tie-navy/10 text-tie-navy border-tie-navy/20",
    accent: "bg-tie-navy",
    bg: "bg-tie-grey-light",
  },
  "professional-proficiency": {
    badge: "bg-tie-gold/10 text-tie-gold-dark border-tie-gold/20",
    accent: "bg-tie-gold",
    bg: "bg-white",
  },
};

export default function CourseDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const course = slug ? findCourseBySlug(slug) : undefined;
  const related = slug ? getRelatedCourses(slug, 3) : [];
  const colors = course ? programmeColors[course.programme] : programmeColors["factory-skills"];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!course) {
    return (
      <main className="min-h-screen bg-tie-grey-light">
        <section className="py-24 md:py-32">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-16 h-16 rounded-full bg-tie-navy/10 flex items-center justify-center mx-auto mb-6">
                <i className="ri-book-open-line text-tie-navy text-2xl" />
              </div>
              <h1 className="font-serif text-2xl md:text-3xl font-bold text-tie-text-primary">
                Course Not Found
              </h1>
              <p className="mt-3 text-base text-tie-text-secondary">
                The course you are looking for does not exist or may have been moved.
              </p>
              <Link
                to="/training"
                className="inline-flex items-center justify-center mt-6 px-6 py-3 rounded-full bg-tie-navy text-white font-medium text-sm hover:bg-tie-navy-deep transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2" />
                Back to Training
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen">
      {/* Hero */}
      <section className={`${colors.bg} py-16 md:py-24 lg:py-32`}>
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <ScrollReveal>
              <Link
                to="/training"
                className="inline-flex items-center gap-2 text-sm font-medium text-tie-text-secondary hover:text-tie-teal transition-colors mb-6 md:mb-8"
              >
                <i className="ri-arrow-left-line" />
                Back to Training Programmes
              </Link>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border mb-4 md:mb-6 ${colors.badge}`}>
                <span className="text-xs font-semibold uppercase tracking-wider">
                  {course.programmeLabel}
                </span>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={150}>
              <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-tie-text-primary leading-tight">
                {course.name}
              </h1>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <p className="mt-4 md:mt-5 text-base md:text-lg text-tie-text-secondary max-w-3xl leading-relaxed">
                {course.description}
              </p>
            </ScrollReveal>

            <ScrollReveal delay={250}>
              <div className="mt-6 md:mt-8 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-tie-teal/10 text-tie-teal text-xs font-semibold">
                  <i className="ri-time-line" />
                  {course.duration}
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-tie-navy/10 text-tie-navy text-xs font-semibold">
                  <i className="ri-user-line" />
                  {course.audience}
                </span>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Topics */}
      <section className="bg-white py-12 md:py-16 lg:py-20">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <ScrollReveal>
              <h2 className="font-serif text-xl md:text-2xl lg:text-3xl font-bold text-tie-text-primary mb-6 md:mb-8">
                What You Will Learn
              </h2>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {course.topics.map((topic, i) => (
                <ScrollReveal key={i} delay={i * 80}>
                  <div className="flex items-start gap-3 p-4 md:p-5 rounded-xl bg-tie-grey-light border border-tie-grey-dark hover:border-tie-teal/30 transition-all duration-300">
                    <div className={`w-8 h-8 rounded-lg ${colors.accent} flex items-center justify-center shrink-0 mt-0.5`}>
                      <span className="text-white text-xs font-bold">{i + 1}</span>
                    </div>
                    <p className="text-sm md:text-base text-tie-text-secondary leading-relaxed">{topic}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Enrollment CTA */}
      <section className="bg-tie-navy py-14 md:py-20">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-white">
                Enrol in This Course
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={100}>
              <p className="mt-3 md:mt-4 text-base md:text-lg text-white/70">
                Join a scheduled cohort or speak with our training advisors to customise a programme for your factory or team.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <div className="mt-7 md:mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/collaborate"
                  className="inline-flex items-center justify-center px-7 py-3.5 rounded-full bg-tie-teal text-white font-medium text-sm hover:bg-tie-teal-dark transition-all duration-300 whitespace-nowrap"
                >
                  Enrol a Cohort
                  <i className="ri-arrow-right-line ml-2" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-7 py-3.5 rounded-full bg-white/10 text-white border border-white/30 font-medium text-sm hover:bg-white/20 transition-all duration-300 whitespace-nowrap"
                >
                  Speak to an Advisor
                  <i className="ri-customer-service-line ml-2" />
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Related Courses */}
      {related.length > 0 && (
        <section className="bg-tie-grey-light py-12 md:py-16 lg:py-20">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <ScrollReveal>
                <h2 className="font-serif text-xl md:text-2xl font-bold text-tie-text-primary mb-6 md:mb-8">
                  Related Courses in {course.programmeLabel}
                </h2>
              </ScrollReveal>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {related.map((rc, i) => (
                  <ScrollReveal key={rc.slug} delay={i * 100}>
                    <Link
                      to={`/training/course/${rc.slug}`}
                      className="group block p-4 md:p-5 rounded-xl bg-white border border-tie-grey-dark hover:border-tie-teal/30 transition-all duration-300"
                    >
                      <span className="text-xs font-semibold text-tie-teal uppercase tracking-wider">
                        {rc.programmeLabel}
                      </span>
                      <h3 className="mt-2 text-sm md:text-base font-semibold text-tie-text-primary group-hover:text-tie-teal transition-colors">
                        {rc.name}
                      </h3>
                      <div className="mt-3 flex items-center gap-3">
                        <span className="text-xs text-tie-text-muted">
                          <i className="ri-time-line mr-1" />
                          {rc.duration}
                        </span>
                        <span className="text-xs text-tie-text-muted">
                          <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </Link>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </main>
  );
}